import moment from 'moment'
import { clone } from 'lodash-es'
import { HOUSE_MATCH_KEY } from '@/constants/property.js'

// Min # of day thresholds for determining which listings are available
const MoveInTimeframes = {
  SHORT: 0,
  MED: 30,
  LONG: 60,
}

const FilterNames = {
  PRICE_MIN: 'priceMin',
  PRICE_MAX: 'priceMax',
  AVAILABLE_ROOMS_MIN: 'availableRoomsMin',
  AVAILABLE_ROOMS_MAX: 'availableRoomsMax',
  MOVE_IN_DATE: 'moveInDate',
  PET_FRIENDLY: 'petFriendly',
  MASTER_BEDROOM: 'masterBedroom',
  PRIVATE_BATHROOM: 'privateBathroom',
  WASHER: 'washer',
  DRYER: 'dryer',
  BACKYARD: 'backyard',
  POOL: 'pool',
  ROOMMATE_INTERESTS: 'roommateInterests',
  HOME_TYPE: 'homeType',
  INSTANT_MATCH: 'instantMatch',
  INSTANT_LEASE: 'instantLease',
}

// return whether or not the given move date matches up with the property's earliest availability
// Short term: Only properties with earliest availability before 30 days
// Medium term: Only properites with earliest availability in 1 month - 2 months**
// Long term: Only properties with earliest availability in > 2 months or vacancy > 75%
// ** Unless they have >= 2 rooms available. Because we only consider earliest available date,
// this can eliminate a lot of properties which may have a second room opening later.
const isAvailableForMoveIn = (moveDate, availabilityDate, availableRoomCount, vacancyPercent) => {
  const shortTermHorizon = moment().add(MoveInTimeframes.MED, 'days')
  const medTermHorizon = moment().add(MoveInTimeframes.LONG, 'days')

  return (
    // Short term
    (moveDate < shortTermHorizon && availabilityDate < shortTermHorizon) ||
    // Med term
    (moveDate >= shortTermHorizon &&
      moveDate < medTermHorizon &&
      ((availabilityDate >= shortTermHorizon && availabilityDate < medTermHorizon) ||
        availableRoomCount >= 2)) ||
    // Long term
    (moveDate >= medTermHorizon && (vacancyPercent >= 0.75 || availabilityDate >= medTermHorizon))
  )
}

const getFilteredProperties = (filtersInitial, annotatedProperties) => {
  const filters = {}
  // convert string booleans to real booleans
  for (let [key, value] of Object.entries(filtersInitial)) {
    filters[key] = ['true', 'false'].includes(value) ? value === 'true' : value
  }

  let filteredProperties = clone(annotatedProperties)

  // Filter by Price Min
  const priceMinFilter = filters[FilterNames.PRICE_MIN]
  if (priceMinFilter || priceMinFilter === 0 || priceMinFilter === '0') {
    filteredProperties = filteredProperties.filter(
      (property) =>
        (property._isGroupHome ? property.fullPropertyPrice : property.priceMin) >= priceMinFilter
    )
  }

  // Filter by Price Max,
  // Respect Min display price of property
  const priceMaxFilter = filters[FilterNames.PRICE_MAX]
  if (priceMaxFilter || priceMaxFilter === 0) {
    filteredProperties = filteredProperties.filter((property) => {
      const priceMin = property._isGroupHome ? property.fullPropertyPrice : property.priceMin
      const priceMax = property._isGroupHome ? property.fullPropertyPrice : property.priceMax
      return priceMax <= priceMaxFilter || priceMin <= priceMaxFilter
    })
  }

  // Filter by MoveInDate
  const moveDateFilter = filters[FilterNames.MOVE_IN_DATE]
  if (moveDateFilter) {
    filteredProperties = filteredProperties.filter((property) => {
      const moveDate = moment(moveDateFilter)
      const availabilityDate = moment(property.earliestAvailableDate)
      return isAvailableForMoveIn(
        moveDate,
        availabilityDate,
        property.availableRoomCount,
        property.vacancyPercent
      )
    })
  }

  // Filter by Available Rooms
  const roomsMinFilter = filters[FilterNames.AVAILABLE_ROOMS_MIN]
  const roomsMaxFilter = filters[FilterNames.AVAILABLE_ROOMS_MAX]
  if (roomsMinFilter && roomsMaxFilter) {
    filteredProperties = filteredProperties.filter(
      (property) =>
        property.availableRoomCount >= roomsMinFilter &&
        property.availableRoomCount <= roomsMaxFilter
    )
  }

  // filter by Master Bedroom Available
  const masterBedroomFilter = filters[FilterNames.MASTER_BEDROOM]
  if (masterBedroomFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.roomAmenities.some((amenity) => {
        return amenity === 'Master Bedroom'
      })
    )
  }

  // filter by Home Type
  // options - co_living or group_living
  const homeTypeFilter = filters[FilterNames.HOME_TYPE]
  if (homeTypeFilter) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyMarketingType === homeTypeFilter
    )
  }

  // filter by Private Bathroom Available
  const privateBathroomFilter = filters[FilterNames.PRIVATE_BATHROOM]
  if (privateBathroomFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.roomAmenities.some((amenity) => {
        return amenity === 'Private Bath'
      })
    )
  }

  // filter by Pet Friendly property
  const petFriendlyFilter = filters[FilterNames.PET_FRIENDLY]
  if (petFriendlyFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.amenities.some((amenity) => {
        return amenity.displayName === 'Pet Friendly'
      })
    )
  }

  const washerFilter = filters[FilterNames.WASHER]
  if (washerFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.amenities.some((amenity) => {
        return amenity.displayName === 'Washer'
      })
    )
  }

  const dryerFilter = filters[FilterNames.DRYER]
  if (dryerFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.amenities.some((amenity) => {
        return amenity.displayName === 'Dryer'
      })
    )
  }

  const backyardFilter = filters[FilterNames.BACKYARD]
  if (backyardFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.amenities.some((amenity) => {
        return amenity.displayName === 'Backyard'
      })
    )
  }
  const poolFilter = filters[FilterNames.POOL]
  if (poolFilter) {
    filteredProperties = filteredProperties.filter((property) =>
      property.amenities.some((amenity) => {
        return amenity.displayName === 'Pool'
      })
    )
  }

  // when the instant match filter is triggered
  // only filter is the key exists on the property
  // see frontend/src/store/modules/property.js property getter
  const instantLease = filters[FilterNames.INSTANT_LEASE]
  if (instantLease)
    filteredProperties = filteredProperties.filter((property) => property.isInstantLease)

  const instantMatch = filters[FilterNames.INSTANT_MATCH]
  if (instantMatch)
    filteredProperties = filteredProperties.filter(
      (property) => !(HOUSE_MATCH_KEY in property) || property._isMatch
    )

  const roommateInterests = filters[FilterNames.ROOMMATE_INTERESTS]
  if (roommateInterests) {
    filteredProperties = filteredProperties
      .filter((property) => {
        const propertyInterests = property.roommateSharedInterests.map((interest) => interest.label)
        // ensure the property has at-least one matching interest
        return roommateInterests.split(',').some((interestFilter) => {
          return propertyInterests.includes(interestFilter)
        })
      })
      // sort properties by the best fit
      .sort((a, b) => {
        return (
          b.roommateSharedInterests.filter(({ label }) =>
            roommateInterests.split(',').includes(label)
          ).length -
          a.roommateSharedInterests.filter(({ label }) =>
            roommateInterests.split(',').includes(label)
          ).length
        )
      })
  }
  return filteredProperties
}

export { FilterNames, isAvailableForMoveIn, getFilteredProperties }
